import React, { useState, useEffect, useCallback } from 'react';
import './StatusTab.css';
import './HealthHourTab.css';
import { PrivacyPolicyModal } from './PrivacyPolicy';
import { TermsOfServiceModal } from './TermsOfService';
import { InstagramGlyph } from './NavIcons';

const fmtDate = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

function PreferencesTab({ svc }) {
  // ---- Nome completo / Idade / Objetivos / limite de análises IA ----
  const [profile, setProfile] = useState(null);
  const [fullName, setFullName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [apelido, setApelido] = useState('');
  const [savingApelido, setSavingApelido] = useState(false);
  const [apelidoSaved, setApelidoSaved] = useState(false);
  const [birth, setBirth] = useState('');
  const [savingBirth, setSavingBirth] = useState(false);
  const [peso, setPeso] = useState('');
  const [savingPeso, setSavingPeso] = useState(false);
  const [pesoSaved, setPesoSaved] = useState(false);
  const [sexo, setSexo] = useState('');
  const [savingSexo, setSavingSexo] = useState(false);
  const [sexoSaved, setSexoSaved] = useState(false);
  const [allergies, setAllergies] = useState('');
  const [savingAllergies, setSavingAllergies] = useState(false);
  const [allergiesSaved, setAllergiesSaved] = useState(false);
  const [fonteTreino, setFonteTreino] = useState('');
  const [savingFonteTreino, setSavingFonteTreino] = useState(false);
  const [analisesPorDia, setAnalisesPorDia] = useState(2);
  const [savingAnalises, setSavingAnalises] = useState(false);
  const [aiKeyMasked, setAiKeyMasked] = useState('');
  const [aiKeyInput, setAiKeyInput] = useState('');
  const [savingAiKey, setSavingAiKey] = useState(false);
  const [aiKeyMsg, setAiKeyMsg] = useState(null);

  const [metaCalorias, setMetaCalorias] = useState('');
  const [metaExercicio, setMetaExercicio] = useState('');
  const [metaEmPe, setMetaEmPe] = useState('');
  const [savingMetas, setSavingMetas] = useState(false);
  const [metasMsg, setMetasMsg] = useState(null);

  const [goals, setGoals] = useState([]);
  const [goalText, setGoalText] = useState('');
  const [goalDate, setGoalDate] = useState('');
  const [goalBusy, setGoalBusy] = useState(false);

  const loadProfileAndGoals = useCallback(async () => {
    try {
      const [p, g] = await Promise.all([
        svc.getProfile().catch(() => null),
        svc.getGoals().catch(() => null),
      ]);
      setProfile(p);
      setFullName(p?.full_name || '');
      setApelido(p?.apelido || '');
      setBirth(p?.birth_date || '');
      setPeso(p?.peso_kg ?? '');
      setSexo(p?.sexo || '');
      setAllergies(p?.alergias_alimentares || '');
      setFonteTreino(p?.preferencia_fonte_treino || '');
      setAnalisesPorDia(p?.analises_por_dia || 2);
      setAiKeyMasked(p?.anthropic_api_key_masked || '');
      setMetaCalorias(p?.meta_calorias ?? '');
      setMetaExercicio(p?.meta_exercicio_min ?? '');
      setMetaEmPe(p?.meta_em_pe_horas ?? '');
      setGoals(g?.goals || []);
    } catch (e) { /* silêncio */ }
  }, [svc]);

  const salvarNome = async () => {
    setSavingName(true);
    try {
      const p = await svc.saveFullName(fullName.trim());
      if (p.error) throw new Error(p.error);
      setProfile(p);
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2000);
    } catch (e) { /* silêncio */ } finally { setSavingName(false); }
  };

  const salvarApelido = async () => {
    setSavingApelido(true);
    try {
      const p = await svc.saveApelido(apelido.trim());
      if (p.error) throw new Error(p.error);
      setProfile(p);
      setApelidoSaved(true);
      setTimeout(() => setApelidoSaved(false), 2000);
    } catch (e) { /* silêncio */ } finally { setSavingApelido(false); }
  };

  const salvarNascimento = async () => {
    setSavingBirth(true);
    try {
      const p = await svc.saveProfile(birth);
      if (p.error) throw new Error(p.error);
      setProfile(p);
    } catch (e) { /* silêncio */ } finally { setSavingBirth(false); }
  };

  const salvarPeso = async () => {
    setSavingPeso(true);
    try {
      const p = await svc.saveWeight(peso === '' ? null : Number(peso));
      if (p.error) throw new Error(p.error);
      setProfile(p);
      setPesoSaved(true);
      setTimeout(() => setPesoSaved(false), 2000);
    } catch (e) { /* silêncio */ } finally { setSavingPeso(false); }
  };

  const salvarSexo = async (valor) => {
    setSexo(valor);
    setSavingSexo(true);
    try {
      const p = await svc.saveSexo(valor || null);
      if (p.error) throw new Error(p.error);
      setProfile(p);
      setSexoSaved(true);
      setTimeout(() => setSexoSaved(false), 2000);
    } catch (e) { /* silêncio */ } finally { setSavingSexo(false); }
  };

  const salvarAlergias = async () => {
    setSavingAllergies(true);
    try {
      const p = await svc.saveFoodAllergies(allergies);
      if (p.error) throw new Error(p.error);
      setAllergiesSaved(true);
      setTimeout(() => setAllergiesSaved(false), 2000);
    } catch (e) { /* silêncio */ } finally { setSavingAllergies(false); }
  };

  const salvarFonteTreino = async (valor) => {
    setFonteTreino(valor);
    setSavingFonteTreino(true);
    try {
      const p = await svc.saveFontePreferidaTreino(valor || null);
      if (p.error) throw new Error(p.error);
      setProfile(p);
    } catch (e) { /* silêncio */ } finally { setSavingFonteTreino(false); }
  };

  const salvarMetas = async () => {
    setSavingMetas(true); setMetasMsg(null);
    try {
      const p = await svc.saveMetasSaude({
        meta_calorias: metaCalorias === '' ? null : Number(metaCalorias),
        meta_exercicio_min: metaExercicio === '' ? null : Number(metaExercicio),
        meta_em_pe_horas: metaEmPe === '' ? null : Number(metaEmPe),
      });
      if (p.error) throw new Error(p.error);
      setMetasMsg('✓ Metas salvas.');
    } catch (e) { setMetasMsg(e.message); } finally { setSavingMetas(false); }
  };

  const salvarAnalisesPorDia = async (valor) => {
    setAnalisesPorDia(valor);
    setSavingAnalises(true);
    try {
      await svc.saveAnalisesPorDia(valor);
    } catch (e) { /* silêncio */ } finally { setSavingAnalises(false); }
  };

  const salvarChaveIA = async () => {
    setSavingAiKey(true); setAiKeyMsg(null);
    try {
      const p = await svc.saveAnthropicApiKey(aiKeyInput.trim());
      if (p.error) throw new Error(p.error);
      setAiKeyMasked(p.anthropic_api_key_masked || '');
      setAiKeyInput('');
      setAiKeyMsg('✓ Chave salva.');
    } catch (e) { setAiKeyMsg(e.message); } finally { setSavingAiKey(false); }
  };

  const removerChaveIA = async () => {
    setSavingAiKey(true); setAiKeyMsg(null);
    try {
      const p = await svc.saveAnthropicApiKey('');
      if (p.error) throw new Error(p.error);
      setAiKeyMasked('');
      setAiKeyMsg('Chave removida — voltou a usar o padrão do sistema.');
    } catch (e) { setAiKeyMsg(e.message); } finally { setSavingAiKey(false); }
  };

  const addGoal = async () => {
    if (!goalText.trim()) return;
    setGoalBusy(true);
    try {
      const r = await svc.createGoal(goalText.trim(), goalDate);
      if (r.error) throw new Error(r.error);
      setGoalText(''); setGoalDate('');
      setGoals((await svc.getGoals()).goals || []);
    } catch (e) { /* silêncio */ } finally { setGoalBusy(false); }
  };

  const toggleGoal = async (id) => {
    try { await svc.toggleGoal(id); setGoals((await svc.getGoals()).goals || []); }
    catch (e) { /* silêncio */ }
  };

  const removeGoal = async (id) => {
    try { await svc.deleteGoal(id); setGoals((await svc.getGoals()).goals || []); }
    catch (e) { /* silêncio */ }
  };

  // ---- E-mails: remetentes por conta ----
  const [efAccounts, setEfAccounts] = useState(null);
  const [efLoading, setEfLoading] = useState(false);
  const [efError, setEfError] = useState(null);
  const [efInputs, setEfInputs] = useState({});
  const [efSaving, setEfSaving] = useState(null);

  const loadEmailFilters = useCallback(async () => {
    setEfLoading(true); setEfError(null);
    try {
      const d = await svc.getEmailFilters();
      if (d.error) throw new Error(d.error);
      setEfAccounts(d.accounts || []);
    } catch (e) { setEfError(e.message); } finally { setEfLoading(false); }
  }, [svc]);

  const persistSenders = async (acc, senders) => {
    setEfSaving(acc.key); setEfError(null);
    try {
      const d = await svc.saveEmailFilters(acc.filter_key, senders);
      if (d.error) throw new Error(d.error);
      setEfAccounts((prev) => prev.map((a) => (a.key === acc.key ? { ...a, senders: d.senders } : a)));
    } catch (e) { setEfError(e.message); } finally { setEfSaving(null); }
  };

  const addSender = async (acc) => {
    const raw = (efInputs[acc.key] || '').trim().toLowerCase();
    if (!raw) return;
    if ((acc.senders || []).includes(raw)) { setEfInputs((p) => ({ ...p, [acc.key]: '' })); return; }
    await persistSenders(acc, [...(acc.senders || []), raw]);
    setEfInputs((p) => ({ ...p, [acc.key]: '' }));
  };

  const removeSender = async (acc, sender) => {
    await persistSenders(acc, (acc.senders || []).filter((s) => s !== sender));
  };

  // ---- Notícias: categorias preferidas ----
  const [newsTopics, setNewsTopics] = useState([]);
  const [newsAvailable, setNewsAvailable] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsSaving, setNewsSaving] = useState(false);
  const [newsCustom, setNewsCustom] = useState('');
  const [newsMsg, setNewsMsg] = useState(null);

  const loadNewsPrefs = useCallback(async () => {
    setNewsLoading(true);
    try {
      const d = await svc.getNewsPreferences();
      if (d.error) throw new Error(d.error);
      setNewsTopics(d.topics || []);
      setNewsAvailable(d.available || []);
    } catch (e) { setNewsMsg(e.message); } finally { setNewsLoading(false); }
  }, [svc]);

  const toggleNewsTopic = (topic) => {
    setNewsTopics((prev) => prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]);
  };

  const addCustomTopic = () => {
    const t = newsCustom.trim().toLowerCase();
    if (t && !newsTopics.includes(t)) setNewsTopics((prev) => [...prev, t]);
    setNewsCustom('');
  };

  const saveNewsTopics = async () => {
    setNewsSaving(true); setNewsMsg(null);
    try {
      const d = await svc.saveNewsPreferences(newsTopics);
      if (d.error) throw new Error(d.error);
      setNewsMsg('Preferências de notícias salvas.');
    } catch (e) { setNewsMsg(e.message); } finally { setNewsSaving(false); }
  };

  // ---- Composição corporal: só o upload aqui (exibição fica no card da Home) ----
  const [bodyUploading, setBodyUploading] = useState(false);
  const [bodyMsg, setBodyMsg] = useState(null);

  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // ---- Instagram ----
  const [igStatus, setIgStatus] = useState(null);
  const [igBusy, setIgBusy] = useState(false);
  const [igMsg, setIgMsg] = useState(null);

  const loadInstagramStatus = useCallback(async () => {
    try { setIgStatus(await svc.getInstagramStatus()); } catch (e) { /* silêncio */ }
  }, [svc]);

  // ---- E-mail self-service (Gmail / Outlook) ----
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [gmailMsg, setGmailMsg] = useState(null);
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [outlookBusy, setOutlookBusy] = useState(false);
  const [outlookMsg, setOutlookMsg] = useState(null);

  const loadEmailAccountsStatus = useCallback(async () => {
    try { setGmailStatus(await svc.getGmailStatus()); } catch (e) { /* silêncio */ }
    try { setOutlookStatus(await svc.getOutlookStatus()); } catch (e) { /* silêncio */ }
  }, [svc]);

  const desconectarGmail = async () => {
    setGmailBusy(true);
    try {
      await svc.disconnectGmail();
      setGmailStatus({ connected: false });
      setGmailMsg('Gmail desconectado.');
    } catch (e) { setGmailMsg(e.message); } finally { setGmailBusy(false); }
  };

  const desconectarOutlook = async () => {
    setOutlookBusy(true);
    try {
      await svc.disconnectOutlook();
      setOutlookStatus({ connected: false });
      setOutlookMsg('Outlook desconectado.');
    } catch (e) { setOutlookMsg(e.message); } finally { setOutlookBusy(false); }
  };

  const desconectarInstagram = async () => {
    setIgBusy(true);
    try {
      await svc.disconnectInstagram();
      setIgStatus({ connected: false });
      setIgMsg('Instagram desconectado.');
    } catch (e) { setIgMsg(e.message); } finally { setIgBusy(false); }
  };

  // O callback OAuth volta pra "/" com ?instagram=conectado|erro — mostra o
  // resultado e limpa o parâmetro da URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ig = params.get('instagram');
    if (ig === 'conectado') setIgMsg('✓ Instagram conectado.');
    else if (ig === 'erro') setIgMsg('Erro ao conectar Instagram. Tente novamente.');
    if (ig) {
      params.delete('instagram');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  // Idem pro callback OAuth do Gmail e do Outlook (?gmail=/?outlook=conectado|erro).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let mudou = false;
    const gmail = params.get('gmail');
    if (gmail === 'conectado') setGmailMsg('✓ Gmail conectado.');
    else if (gmail === 'erro') setGmailMsg('Erro ao conectar Gmail. Tente novamente.');
    if (gmail) { params.delete('gmail'); mudou = true; }
    const outlook = params.get('outlook');
    if (outlook === 'conectado') setOutlookMsg('✓ Outlook conectado.');
    else if (outlook === 'erro') setOutlookMsg('Erro ao conectar Outlook. Tente novamente.');
    if (outlook) { params.delete('outlook'); mudou = true; }
    if (mudou) {
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  const handleBodyFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBodyUploading(true); setBodyMsg(null);
    try {
      const d = await svc.uploadBodyComposition(file);
      if (d.error) throw new Error(d.error);
      setBodyMsg(`${d.records_saved} registro(s) importado(s). Confira o card na Home.`);
    } catch (e2) { setBodyMsg('Erro: ' + e2.message); } finally { setBodyUploading(false); }
  };

  useEffect(() => {
    loadEmailFilters();
    loadNewsPrefs();
    loadProfileAndGoals();
    loadInstagramStatus();
    loadEmailAccountsStatus();
  }, [loadEmailFilters, loadNewsPrefs, loadProfileAndGoals, loadInstagramStatus, loadEmailAccountsStatus]);

  return (
    <div className="ef-view">
      {/* ===== Nome completo ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">👤 Nome completo</span></div>
        <div className="hh-form">
          <input
            className="hh-input hh-input-wide" placeholder="Seu nome completo"
            value={fullName} onChange={(e) => setFullName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') salvarNome(); }}
          />
          <button className="hh-btn" onClick={salvarNome} disabled={savingName}>
            {savingName ? '…' : nameSaved ? '✓ Salvo' : 'Salvar'}
          </button>
        </div>
        <p className="ef-intro" style={{ margin: '8px 0 0' }}>Usado nas iniciais do cabeçalho da tela principal.</p>
      </div>

      {/* ===== Como gostaria de ser chamado ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">💬 Como gostaria de ser chamado?</span></div>
        <div className="hh-form">
          <input
            className="hh-input hh-input-wide" placeholder="Ex: Cris"
            value={apelido} onChange={(e) => setApelido(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') salvarApelido(); }}
          />
          <button className="hh-btn" onClick={salvarApelido} disabled={savingApelido}>
            {savingApelido ? '…' : apelidoSaved ? '✓ Salvo' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* ===== Idade ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">🎂 Idade</span></div>
        <div className="hh-form">
          <input
            className="hh-input" type="date" value={birth}
            onChange={(e) => setBirth(e.target.value)}
          />
          <button className="hh-btn" onClick={salvarNascimento} disabled={savingBirth}>
            {savingBirth ? '…' : 'Salvar'}
          </button>
        </div>
        <p className="ef-intro" style={{ margin: '8px 0 0' }}>
          {profile?.age != null
            ? `Data de nascimento cadastrada — você tem ${profile.age} anos.`
            : 'Cadastre sua data de nascimento para calcular a idade.'}
        </p>
      </div>

      {/* ===== Peso ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">⚖️ Peso</span></div>
        <div className="hh-form">
          <input
            className="hh-input" type="number" min={1} max={500} step="0.1" placeholder="ex: 78.5"
            value={peso} onChange={(e) => setPeso(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') salvarPeso(); }}
            style={{ maxWidth: 120 }}
          />
          <span className="ef-intro" style={{ margin: 0 }}>kg</span>
          <button className="hh-btn" onClick={salvarPeso} disabled={savingPeso}>
            {savingPeso ? '…' : pesoSaved ? '✓ Salvo' : 'Salvar'}
          </button>
        </div>
        <p className="ef-intro" style={{ margin: '8px 0 0' }}>Usado como contexto nas análises de IA.</p>
      </div>

      {/* ===== Sexo ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">⚧ Sexo</span></div>
        <div className="ef-chips">
          {[
            { valor: 'M', label: 'Masculino' },
            { valor: 'F', label: 'Feminino' },
          ].map((op) => (
            <button
              key={op.valor}
              className="ef-chip"
              style={{
                cursor: 'pointer', border: sexo === op.valor ? '1px solid var(--accent-primary)' : undefined,
                background: sexo === op.valor ? 'var(--accent-primary)' : undefined,
              }}
              disabled={savingSexo}
              onClick={() => salvarSexo(op.valor)}
            >
              {sexo === op.valor ? '✓ ' : ''}{op.label}
            </button>
          ))}
        </div>
        <p className="ef-intro" style={{ margin: '8px 0 0' }}>
          {sexoSaved ? '✓ Salvo.' : 'Usado para estimar calorias gastas na academia com mais precisão.'}
        </p>
      </div>

      {/* ===== Alergias e restrições alimentares ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">🍽️ Alergias e Restrições Alimentares</span></div>
        <div className="hh-form">
          <input
            className="hh-input hh-input-wide" placeholder="Ex: amendoim, camarão, lactose"
            value={allergies} onChange={(e) => setAllergies(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') salvarAlergias(); }}
          />
          <button className="hh-btn" onClick={salvarAlergias} disabled={savingAllergies}>
            {savingAllergies ? '…' : allergiesSaved ? '✓ Salvo' : 'Salvar'}
          </button>
        </div>
        <p className="ef-intro" style={{ margin: '8px 0 0' }}>A IA usa isso pra não incluir esses alimentos no cardápio sugerido.</p>
      </div>

      {/* ===== Fonte preferida de treino (Apple Watch x pulseira) ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">⌚ Fonte preferida de treino</span></div>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Se você usa relógio e pulseira (ex: Fitbit) ao mesmo tempo, às vezes
          os dois registram o mesmo treino com números diferentes. Escolha
          qual fonte priorizar quando isso acontecer — a outra some só desse
          treino específico, sem apagar nada.
        </p>
        <div className="ef-chips">
          {[
            { valor: '', label: 'Mostrar os dois' },
            { valor: 'apple', label: 'Apple Watch' },
            { valor: 'outro', label: 'Outro dispositivo (pulseira)' },
          ].map((op) => (
            <button
              key={op.valor}
              className="ef-chip"
              style={{
                cursor: 'pointer', border: fonteTreino === op.valor ? '1px solid var(--accent-primary)' : undefined,
                background: fonteTreino === op.valor ? 'var(--accent-primary)' : undefined,
              }}
              disabled={savingFonteTreino}
              onClick={() => salvarFonteTreino(op.valor)}
            >
              {fonteTreino === op.valor ? '✓ ' : ''}{op.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== Objetivos ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">🎯 Objetivos</span></div>
        <div className="hh-form">
          <input
            className="hh-input hh-input-wide" placeholder="Ex: emagrecer 5 quilos em 3 meses"
            value={goalText} onChange={(e) => setGoalText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addGoal(); }}
          />
          <input
            className="hh-input" type="date" value={goalDate}
            onChange={(e) => setGoalDate(e.target.value)}
          />
          <button className="hh-btn" onClick={addGoal} disabled={goalBusy || !goalText.trim()}>
            {goalBusy ? '…' : 'Adicionar'}
          </button>
        </div>
        {goals.length === 0 && <p className="ef-intro" style={{ margin: '8px 0 0' }}>Nenhum objetivo cadastrado ainda.</p>}
        {goals.map((g) => (
          <div className={`hh-goal ${g.done ? 'done' : ''}`} key={g.id}>
            <button className={`hh-goal-check ${g.done ? 'on' : ''}`} onClick={() => toggleGoal(g.id)}>
              {g.done ? '✓' : ''}
            </button>
            <span className="hh-goal-text">
              {g.description}
              {g.target_date && <small> · até {fmtDate(g.target_date)}</small>}
            </span>
            <button className="hh-goal-del" onClick={() => removeGoal(g.id)} aria-label="Excluir">✕</button>
          </div>
        ))}
      </div>

      {/* ===== Metas de saúde ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">🔥 Metas de saúde</span></div>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Usadas pra comparar com o que a Apple Watch importa (o Health Auto
          Export não manda a meta configurada no relógio, só o valor realizado).
        </p>
        <div className="hh-form">
          <label className="ef-intro" style={{ margin: 0, minWidth: 90 }}>Calorias</label>
          <input
            className="hh-input" type="number" min={0} placeholder="ex: 500"
            value={metaCalorias} onChange={(e) => setMetaCalorias(e.target.value)}
            style={{ maxWidth: 120 }}
          />
        </div>
        <div className="hh-form">
          <label className="ef-intro" style={{ margin: 0, minWidth: 90 }}>Exercício (min)</label>
          <input
            className="hh-input" type="number" min={0} placeholder="ex: 60"
            value={metaExercicio} onChange={(e) => setMetaExercicio(e.target.value)}
            style={{ maxWidth: 120 }}
          />
        </div>
        <div className="hh-form">
          <label className="ef-intro" style={{ margin: 0, minWidth: 90 }}>Em pé (h)</label>
          <input
            className="hh-input" type="number" min={0} placeholder="ex: 12"
            value={metaEmPe} onChange={(e) => setMetaEmPe(e.target.value)}
            style={{ maxWidth: 120 }}
          />
        </div>
        <button className="hh-btn" onClick={salvarMetas} disabled={savingMetas}>
          {savingMetas ? '…' : 'Salvar metas'}
        </button>
        {metasMsg && <p className="ef-msg" style={{ marginTop: 8 }}>{metasMsg}</p>}
      </div>

      {/* ===== Instagram ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account"><InstagramGlyph /> Instagram</span></div>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Conecte sua conta profissional do Instagram pra ver seguidores,
          alcance e visualizações de perfil num card na tela principal.
        </p>
        {igStatus?.connected ? (
          <>
            <p className="ef-intro" style={{ margin: '0 0 12px' }}>
              Conectado como <b>@{igStatus.username}</b>.
            </p>
            <button className="hh-btn" onClick={desconectarInstagram} disabled={igBusy}>
              {igBusy ? '…' : 'Desconectar'}
            </button>
          </>
        ) : (
          <a
            className="ef-add-btn"
            style={{ display: 'inline-block', textDecoration: 'none' }}
            href={svc.instagramConnectUrl()}
          >
            Conectar Instagram
          </a>
        )}
        {igMsg && <p className="ef-msg" style={{ marginTop: 8 }}>{igMsg}</p>}
      </div>

      {/* ===== Análise de IA: quantas vezes por dia ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">🧠 Análise de IA</span></div>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Quantas vezes por dia você pode gerar a análise de IA (padrão 2x/dia).
        </p>
        <div className="hh-form">
          <input
            className="hh-input" type="number" min={1} max={10} value={analisesPorDia}
            onChange={(e) => salvarAnalisesPorDia(Number(e.target.value) || 1)}
            style={{ maxWidth: 90 }}
          />
          <span className="ef-intro" style={{ margin: 0 }}>{savingAnalises ? 'Salvando…' : 'vezes ao dia'}</span>
        </div>
      </div>

      {/* ===== Chave própria da IA (BYOK) ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">🔑 Chave Anthropic (IA)</span></div>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Opcional. Se você colar a sua própria chave da API da Anthropic
          (<a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">console.anthropic.com</a>),
          a Análise de IA, o cardápio e as sugestões de treino passam a
          usar ela — sem isso, usa a chave padrão do sistema.
        </p>
        {aiKeyMasked && (
          <p className="ef-intro" style={{ margin: '0 0 12px' }}>
            Chave atual: <b>{aiKeyMasked}</b>
          </p>
        )}
        <div className="hh-form">
          <input
            className="hh-input hh-input-wide" type="password" placeholder="sk-ant-..."
            value={aiKeyInput} onChange={(e) => setAiKeyInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && aiKeyInput.trim()) salvarChaveIA(); }}
          />
          <button className="hh-btn" onClick={salvarChaveIA} disabled={savingAiKey || !aiKeyInput.trim()}>
            {savingAiKey ? '…' : 'Salvar'}
          </button>
          {aiKeyMasked && (
            <button className="hh-btn" onClick={removerChaveIA} disabled={savingAiKey}>
              Remover
            </button>
          )}
        </div>
        {aiKeyMsg && <p className="ef-msg" style={{ marginTop: 8 }}>{aiKeyMsg}</p>}
      </div>

      {/* ===== Notícias ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">📰 Preferências de notícias</span></div>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Escolha os temas que você quer ver no card de notícias do dashboard.
        </p>
        {newsLoading && <p className="ef-msg">Carregando…</p>}
        <div className="ef-chips">
          {newsAvailable.map((topic) => (
            <button
              key={topic}
              className="ef-chip"
              style={{
                cursor: 'pointer', border: newsTopics.includes(topic) ? '1px solid var(--accent-primary)' : undefined,
                background: newsTopics.includes(topic) ? 'var(--accent-primary)' : undefined,
              }}
              onClick={() => toggleNewsTopic(topic)}
            >
              {newsTopics.includes(topic) ? '✓ ' : ''}{topic}
            </button>
          ))}
          {newsTopics.filter((t) => !newsAvailable.includes(t)).map((topic) => (
            <span className="ef-chip" key={topic}>
              {topic}
              <button className="ef-chip-x" onClick={() => toggleNewsTopic(topic)} aria-label={`Remover ${topic}`}>×</button>
            </span>
          ))}
        </div>
        <div className="ef-add">
          <input
            className="ef-input" placeholder="Adicionar outro tema (ex: economia)"
            value={newsCustom} onChange={(e) => setNewsCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCustomTopic(); }}
          />
          <button className="ef-add-btn" onClick={addCustomTopic}>Adicionar</button>
        </div>
        <button className="ef-add-btn" style={{ marginTop: 12, width: '100%' }} onClick={saveNewsTopics} disabled={newsSaving}>
          {newsSaving ? 'Salvando…' : 'Salvar preferências de notícias'}
        </button>
        {newsMsg && <p className="ef-msg" style={{ marginTop: 8 }}>{newsMsg}</p>}
      </div>

      {/* ===== Composição corporal: upload do Excel ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">⚖️ Composição corporal</span></div>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Exporte o Excel de medições da sua balança e envie o arquivo aqui. O
          resumo e o histórico aparecem em um card na tela principal.
        </p>
        <label className="ef-add-btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
          {bodyUploading ? 'Enviando…' : '📤 Enviar Excel (.xlsx)'}
          <input type="file" accept=".xlsx" onChange={handleBodyFile} disabled={bodyUploading} style={{ display: 'none' }} />
        </label>
        {bodyMsg && <p className="ef-msg" style={{ marginTop: 8 }}>{bodyMsg}</p>}
      </div>

      {/* ===== Conectar conta de e-mail (Gmail / Outlook) ===== */}
      <div className="ef-card">
        <div className="ef-card-head"><span className="ef-account">📧 Conectar sua conta de e-mail</span></div>
        <p className="ef-intro" style={{ margin: '0 0 12px' }}>
          Conecte o Gmail e/ou o Outlook pra ver seus e-mails importantes
          direto no dashboard.
        </p>

        <div className="hh-form" style={{ marginBottom: 4 }}>
          {gmailStatus?.connected ? (
            <>
              <span className="ef-intro" style={{ margin: 0 }}>
                Gmail: <b>{gmailStatus.email}</b>
              </span>
              <button className="hh-btn" onClick={desconectarGmail} disabled={gmailBusy}>
                {gmailBusy ? '…' : 'Desconectar'}
              </button>
            </>
          ) : (
            <a
              className="ef-add-btn"
              style={{ display: 'inline-block', textDecoration: 'none' }}
              href={svc.gmailConnectUrl()}
            >
              Conectar Gmail
            </a>
          )}
        </div>
        {gmailMsg && <p className="ef-msg" style={{ marginBottom: 8 }}>{gmailMsg}</p>}

        <div className="hh-form">
          {outlookStatus?.connected ? (
            <>
              <span className="ef-intro" style={{ margin: 0 }}>
                Outlook: <b>{outlookStatus.email}</b>
              </span>
              <button className="hh-btn" onClick={desconectarOutlook} disabled={outlookBusy}>
                {outlookBusy ? '…' : 'Desconectar'}
              </button>
            </>
          ) : (
            <a
              className="ef-add-btn"
              style={{ display: 'inline-block', textDecoration: 'none' }}
              href={svc.outlookConnectUrl()}
            >
              Conectar Outlook
            </a>
          )}
        </div>
        {outlookMsg && <p className="ef-msg" style={{ marginTop: 8 }}>{outlookMsg}</p>}
      </div>

      {/* ===== E-mails ===== */}
      <p className="ef-intro">
        Cadastre os remetentes que devem aparecer no dashboard. Só e-mails
        destes endereços (recebidos hoje) são mostrados por conta.
      </p>
      {efLoading && !efAccounts && <p className="ef-msg">Carregando…</p>}
      {efError && <p className="ef-msg ef-err">{efError}</p>}
      {efAccounts && efAccounts.length === 0 && <p className="ef-msg">Nenhuma conta de e-mail configurada.</p>}
      {efAccounts && efAccounts.map((acc) => (
        <div className="ef-card" key={acc.key}>
          <div className="ef-card-head">
            <span className="ef-account">{acc.account}</span>
            <span className="ef-badge">{(acc.senders || []).length}</span>
          </div>
          <div className="ef-chips">
            {(acc.senders || []).length === 0 && (
              <span className="ef-empty">Sem remetentes — mostra todos de hoje.</span>
            )}
            {(acc.senders || []).map((s) => (
              <span className="ef-chip" key={s}>
                {s}
                <button className="ef-chip-x" onClick={() => removeSender(acc, s)} disabled={efSaving === acc.key} aria-label={`Remover ${s}`}>×</button>
              </span>
            ))}
          </div>
          <div className="ef-add">
            <input
              className="ef-input" type="email" placeholder="novo@remetente.com"
              value={efInputs[acc.key] || ''}
              onChange={(e) => setEfInputs((p) => ({ ...p, [acc.key]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') addSender(acc); }}
              disabled={efSaving === acc.key}
            />
            <button className="ef-add-btn" onClick={() => addSender(acc)} disabled={efSaving === acc.key}>
              {efSaving === acc.key ? '…' : 'Adicionar'}
            </button>
          </div>
        </div>
      ))}

      {/* ===== Política de Privacidade ===== */}
      <button
        className="ef-add-btn"
        style={{ width: '100%', marginTop: 4 }}
        onClick={() => setShowPrivacy(true)}
      >
        📄 Política de Privacidade
      </button>
      {showPrivacy && <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />}

      {/* ===== Termos de Serviço ===== */}
      <button
        className="ef-add-btn"
        style={{ width: '100%', marginTop: 8 }}
        onClick={() => setShowTerms(true)}
      >
        📄 Termos de Serviço
      </button>
      {showTerms && <TermsOfServiceModal onClose={() => setShowTerms(false)} />}

      <div className="spacer"></div>
    </div>
  );
}

export default PreferencesTab;
